import { createElement as h, useRef, useState } from 'react';
import './ManoeuvresPanel.css';

/** Data comes from the authorised learning library, never fabricated content.
 * onResume must resolve only after the parent has recalculated the lesson route.
 * Keeping the map mounted underneath preserves navigation and deadline checks.
 */
export default function ManoeuvresPanel({ skills = [], initialSkillId = '', onAsk, onResume, onSelect, libraryStatus, onRetry }) {
  const [skillId, setSkillId] = useState(initialSkillId);
  const [mode, setMode] = useState('read');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const resumePending = useRef(false);
  const skill = skills.find(item => item.id === skillId);
  const video = skill?.videoUrl;
  const safeVideo = typeof video === 'string' && /^https:\/\//i.test(video) ? video : null;
  const resume = async () => {
    if (resumePending.current || typeof onResume !== 'function') return;
    resumePending.current = true;
    setBusy(true);
    setError('');
    try { await onResume(); }
    catch { setError('The route could not be updated. Try again when ready.'); }
    finally { resumePending.current = false; setBusy(false); }
  };
  return h('section', { className: 'ndg-manoeuvres', 'aria-label': 'Manoeuvres learning', 'data-ndg-map-overlay': 'true' },
    h('header', null,
      h('h2', null, 'Manoeuvres'),
      h('button', { type: 'button', onClick: resume, disabled: busy || !onResume }, busy ? 'Updating route…' : 'Back to sat-nav')),
    h('p', null, 'While safely parked, choose a skill to read, watch or discuss with Karen.'),
    h('label', null, 'Driving skill', h('select', {
      value: skillId, onChange: event => { setSkillId(event.target.value); onSelect?.(event.target.value); setMode('read'); setError(''); },
    }, h('option', { value: '' }, 'Choose a skill'), skills.map(item => h('option', { key: item.id, value: item.id }, item.label)))),
    h('div', { className: 'ndg-manoeuvres-options', 'aria-label': 'Ways to learn' },
      h('button', { type: 'button', 'aria-pressed': mode === 'read', disabled: !skill, onClick: () => setMode('read') }, 'Read'),
      h('button', { type: 'button', 'aria-pressed': mode === 'watch', disabled: !skill, onClick: () => setMode('watch') }, 'Watch'),
      h('button', { type: 'button', disabled: !skill || !onAsk, onClick: async () => { try { await onAsk(skill); } catch { setError('Karen is unavailable. Please try the main conversation input.'); } } }, 'Ask Karen')),
    skill && h('article', null,
      h('h3', null, skill.label),
      mode === 'read'
        ? h('p', { className: 'ndg-manoeuvres-reading' }, skill.text || 'No reading material is available for this skill yet.')
        : safeVideo ? h('video', { key: skill.id, src: safeVideo, controls: true, playsInline: true, preload: 'none', 'aria-label': skill.label })
          : h('p', null, 'No video is available for this skill yet. You can read or ask Karen.')),
    libraryStatus && h('p', { role: 'status' }, libraryStatus),
    onRetry && h('button', { type: 'button', onClick: onRetry }, 'Retry learning library'),
    !skills.length && !libraryStatus && h('p', { role: 'status' }, 'Learning resources are not available yet.'),
    error && h('p', { role: 'alert' }, error));
}
